import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2, LayoutGrid, AlignLeft, Search, Users, Rows } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { fetchRooms, createRoom, updateRoom, deleteRoom } from '../store/roomSlice';
import { useConfirm } from '../context/ConfirmContext';

const getAdminId = () => {
  return Cookies.get('adminId') || '6a6adad6068ce446beb73a07';
};

const RoomMaster = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  
  const { rooms, loading } = useSelector((state) => state.room);
  
  const [currentName, setCurrentName] = useState('');
  const [currentDesc, setCurrentDesc] = useState('');
  const [currentRows, setCurrentRows] = useState('');
  const [currentCapacity, setCurrentCapacity] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRooms = (rooms || []).filter(room => 
    room.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    room.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    dispatch(fetchRooms(getAdminId()));
  }, [dispatch]);

  const handleSave = async () => {
    if (!currentName.trim()) {
      alert('Room / Lab Name is required');
      return;
    }
    const adminId = getAdminId();
    if (editingId) {
      await dispatch(updateRoom({ 
        id: editingId, 
        data: { 
          adminId, 
          name: currentName, 
          description: currentDesc,
          totalRows: currentRows,
          capacity: currentCapacity
        } 
      }));
      setEditingId(null);
    } else {
      await dispatch(createRoom({ 
        adminId, 
        name: currentName, 
        description: currentDesc,
        totalRows: currentRows,
        capacity: currentCapacity
      }));
    }
    setCurrentName('');
    setCurrentDesc('');
    setCurrentRows('');
    setCurrentCapacity('');
  };

  const handleEdit = (room) => {
    setEditingId(room._id);
    setCurrentName(room.name || '');
    setCurrentDesc(room.description || '');
    setCurrentRows(room.totalRows || '');
    setCurrentCapacity(room.capacity || '');
  };

  const handleDelete = async (id) => {
    const isConfirmed = await confirm('Are you sure you want to delete this room/lab?');
    if (!isConfirmed) return;
    await dispatch(deleteRoom({ id, adminId: getAdminId() }));
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setCurrentName('');
    setCurrentDesc('');
    setCurrentRows('');
    setCurrentCapacity('');
  };

  return (
    <div style={{ padding: '24px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <button 
            onClick={() => navigate('/masters')}
            style={{
              background: 'var(--input-bg)',
              border: '1px solid var(--border-light)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: 14,
              color: 'var(--text-primary)',
              padding: '8px 16px',
              height: 'fit-content'
            }}
          >
            <ArrowLeft size={16} />
            All Masters
          </button>
          <div>
            <h1 style={{ margin: '0 0 4px 0', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>Room & Lab Master</h1>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>Create, update and manage physical office rooms, labs and interactive grids.</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, flex: 1, flexWrap: 'wrap' }}>
        {/* Left Pane - Form */}
        <div className="dash-glass-card" style={{ flex: '1 1 350px', maxWidth: '100%', padding: 24, display: 'flex', flexDirection: 'column', gap: 24, height: 'fit-content' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
            {editingId ? 'Edit Room / Lab' : 'Create Room / Lab'}
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Room / Lab Name *</label>
              <div style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                padding: '12px 16px',
                display: 'flex',
                gap: 12,
                alignItems: 'center'
              }}>
                <LayoutGrid size={16} color="var(--accent-primary)" />
                <input 
                  type="text"
                  placeholder="e.g. Lab 1, Conference Room A"
                  value={currentName}
                  onChange={(e) => setCurrentName(e.target.value)}
                  style={{ 
                    background: 'transparent', 
                    border: 'none', 
                    color: 'var(--text-primary)', 
                    fontSize: 14, 
                    width: '100%',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Description / Location</label>
              <div style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                padding: '12px 16px',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start'
              }}>
                <AlignLeft size={16} color="var(--accent-primary)" style={{ marginTop: 2 }} />
                <textarea 
                  placeholder="Optional description"
                  value={currentDesc}
                  onChange={(e) => setCurrentDesc(e.target.value)}
                  style={{ 
                    background: 'transparent', 
                    border: 'none', 
                    color: 'var(--text-primary)', 
                    fontSize: 14, 
                    width: '100%',
                    resize: 'none',
                    minHeight: 60,
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total Rows</label>
                <div style={{
                  background: 'var(--input-bg)',
                  border: '1px solid var(--border-light)',
                  borderRadius: 8,
                  padding: '12px 16px',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center'
                }}>
                  <Rows size={16} color="var(--accent-primary)" />
                  <input 
                    type="number"
                    placeholder="e.g. 5"
                    value={currentRows}
                    onChange={(e) => setCurrentRows(e.target.value)}
                    style={{ 
                      background: 'transparent', 
                      border: 'none', 
                      color: 'var(--text-primary)', 
                      fontSize: 14, 
                      width: '100%',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Capacity</label>
                <div style={{
                  background: 'var(--input-bg)',
                  border: '1px solid var(--border-light)',
                  borderRadius: 8,
                  padding: '12px 16px',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center'
                }}>
                  <Users size={16} color="var(--accent-primary)" />
                  <input 
                    type="number"
                    placeholder="e.g. 30"
                    value={currentCapacity}
                    onChange={(e) => setCurrentCapacity(e.target.value)}
                    style={{ 
                      background: 'transparent', 
                      border: 'none', 
                      color: 'var(--text-primary)', 
                      fontSize: 14, 
                      width: '100%',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 'auto' }}>
            <button 
              onClick={handleCancelEdit}
              style={{
                flex: 1,
                padding: '10px',
                background: 'var(--input-bg)',
                border: '1px solid var(--border-light)',
                borderRadius: 8,
                color: 'var(--text-primary)',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              className="btn-primary"
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: 8,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {editingId ? 'Update' : 'Save'}
            </button>
          </div>
        </div>

        {/* Right Pane - List */}
        <div className="dash-glass-card" style={{ flex: '2 1 400px', minWidth: 0, padding: 24, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: 'fit-content', maxHeight: 'calc(100vh - 160px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              background: 'var(--input-bg)', 
              border: '1px solid var(--border-light)', 
              borderRadius: 8, 
              padding: '8px 16px',
              width: '100%',
              maxWidth: 300,
              gap: 12
            }}>
              <Search size={16} color="var(--text-muted)" />
              <input 
                type="text" 
                placeholder="Search rooms..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: 'var(--text-primary)',
                  outline: 'none',
                  width: '100%'
                }}
              />
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Registered Rooms: <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>{rooms?.length || 0}</span>
            </div>
          </div>

          <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, paddingRight: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)', width: 80 }}>Index</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)' }}>Room Name</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)' }}>Description</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)' }}>Rows/Cap</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid var(--border-light)', width: 100, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>Loading...</td>
                  </tr>
                ) : filteredRooms.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>No rooms or labs found.</td>
                  </tr>
                ) : (
                  filteredRooms.map((room, index) => (
                    <tr key={room._id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 13 }}>#{index + 1}</td>
                      <td style={{ padding: '16px', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <LayoutGrid size={14} color="var(--accent-primary)" />
                          {room.name}
                        </div>
                      </td>
                      <td style={{ padding: '16px', color: 'var(--text-secondary)', fontSize: 13 }}>
                        {room.description || '-'}
                      </td>
                      <td style={{ padding: '16px', color: 'var(--text-secondary)', fontSize: 13 }}>
                        {room.totalRows || '-'} / {room.capacity || '-'}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button 
                            onClick={() => handleEdit(room)}
                            style={{ 
                              background: 'var(--input-bg)', 
                              border: '1px solid var(--border-light)', 
                              padding: '6px', 
                              borderRadius: 4, 
                              color: 'var(--text-primary)',
                              cursor: 'pointer' 
                            }}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button 
                            onClick={() => handleDelete(room._id)}
                            style={{ 
                              background: 'rgba(239, 68, 68, 0.1)', 
                              border: '1px solid rgba(239, 68, 68, 0.3)', 
                              padding: '6px', 
                              borderRadius: 4, 
                              color: '#EF4444',
                              cursor: 'pointer' 
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomMaster;
